/**
 * Façade graphiques Plotly « Homeys » : layout DAPI / axes / légende + survol traces.
 * S’appuie sur {@link ../plotlyDapiOptimTurpeLayout} pour garder une seule source de vérité
 * tout en simplifiant les imports dans les modules métiers (PV, courtage, etc.).
 */

import type { PlotlyGraph } from "@modules/data/components/data/chart/PlotlyChartCard.vue"
import {
    DEFAULT_HOVER_Y_FORMAT,
    enrichHistoriqueTraceHover,
    enrichLayoutWithOptimTurpeAxes,
    enrichTracesHover,
    extractTooltipUnitFromAxisLabel,
    mergeDapiHoverLayout,
    validateHoverYFormat,
    type EnrichOptimTurpeLayoutExtras,
    type PlotlyLayoutAxisChartMeta,
} from "../plotlyDapiOptimTurpeLayout"

export type HomeysPlotlyChartMeta = PlotlyLayoutAxisChartMeta

export type HomeysPlotlyLayoutExtras = EnrichOptimTurpeLayoutExtras

export type ApplyHomeysStandardTraceHoverOptions = {
    /** Métadonnées chart (ex. `historique_puissance` → template survol date + Y). */
    chart?: PlotlyLayoutAxisChartMeta
    /** Libellé axe Y brut (ex. `Énergie (kWh)`). Déduit de `chart.y_label` si absent. */
    yAxisLabel?: string
}

/**
 * Applique le survol DAPI standard sur les traces, puis le template « historique puissance »
 * si `chart` est fourni et correspond à ce cas.
 */
export function applyHomeysStandardTraceHover(
    traces: object[],
    options: ApplyHomeysStandardTraceHoverOptions
): object[] {
    const label =
        options.yAxisLabel?.trim() ?? options.chart?.y_label?.trim() ?? ""
    const yTooltip = extractTooltipUnitFromAxisLabel(label)
    const withDapi = enrichTracesHover(traces, yTooltip)
    if (options.chart) {
        return enrichHistoriqueTraceHover(withDapi, options.chart, yTooltip)
    }
    return withDapi
}

/**
 * Fusionne le fond survol DAPI, puis marges / axes / légende uniformes (Optim Turpe / data).
 */
export function buildHomeysStandardPlotlyLayout(
    partialLayout: Record<string, unknown>,
    chart: HomeysPlotlyChartMeta,
    extras?: HomeysPlotlyLayoutExtras
): Record<string, unknown> {
    return enrichLayoutWithOptimTurpeAxes(
        mergeDapiHoverLayout(partialLayout),
        chart,
        extras
    )
}

/**
 * Tooltip barres : valeur Y formatée, unité optionnelle, nom du tracé (sans date sur l’axe X).
 */
export function buildHomeysBarHoverTemplateYAndTraceName(options: {
    yFormat?: string
    unit: string
}): string {
    const fmt = validateHoverYFormat(options.yFormat ?? DEFAULT_HOVER_Y_FORMAT)
    const unitPart = options.unit.trim() ? ` ${options.unit.trim()}` : ""
    return `<b>%{y:${fmt}}</b>${unitPart}<br>%{fullData.name}<extra></extra>`
}

/**
 * Pipeline complet pour un graphique déjà `{ data, layout }` (ex. bundle Plotly JSON API).
 */
export function finalizeHomeysPlotlyGraph(
    graph: PlotlyGraph,
    chart: HomeysPlotlyChartMeta,
    layoutExtras?: HomeysPlotlyLayoutExtras
): PlotlyGraph {
    return {
        data: applyHomeysStandardTraceHover(graph.data, {
            chart,
            yAxisLabel: chart.y_label ?? "",
        }),
        layout: buildHomeysStandardPlotlyLayout(
            graph.layout as Record<string, unknown>,
            chart,
            layoutExtras
        ),
    }
}
